import { useCallback, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { ColumnDef } from '@tanstack/react-table'
import { DrawerItem, MobileApp } from '@mobileTypes'
import { Add as AddIcon } from '@mui/icons-material'
import { Button, Grid } from '@mui/material'

import { ReactTable } from '@components/ui/ReactTable'

import { DrawerItemDialog } from './DrawerItemDialog'

export const DrawerItemsTable = () => {
  const { watch, setValue } = useFormContext<MobileApp>()
  const drawerItems = watch('drawerItems') || []

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingItem, setEditingItem] = useState<DrawerItem | null>(null)

  const handleAdd = () => {
    setEditingIndex(null)
    setEditingItem({ internalId: '', label: '', icon: null })
    setDialogOpen(true)
  }

  const handleEdit = (index: number) => {
    setEditingIndex(index)
    setEditingItem(drawerItems[index])
    setDialogOpen(true)
  }

  const handleDelete = (index: number) => {
    const newItems = drawerItems.filter((_, i) => i !== index)
    setValue('drawerItems', newItems, { shouldDirty: true })
  }

  const handleSave = (item: DrawerItem) => {
    if (editingIndex !== null) {
      const newItems = [...drawerItems]
      newItems[editingIndex] = item
      setValue('drawerItems', newItems, { shouldDirty: true })
    } else {
      setValue('drawerItems', [...drawerItems, item], { shouldDirty: true })
    }
    setDialogOpen(false)
  }

  const handleDropChange = useCallback(
    (rows: DrawerItem[], _row: DrawerItem, _newIndex: number) => {
      setValue('drawerItems', rows, { shouldDirty: true })
      return rows
    },
    [setValue]
  )

  const columns = useMemo<ColumnDef<DrawerItem>[]>(
    () => [
      {
        accessorKey: 'internalId',
        header: 'Internal ID',
        size: 180,
        cell: ({ getValue }) => getValue(),
      },
      {
        accessorKey: 'label',
        header: 'Label',
        meta: { flex: 1 },
        size: 220,
        cell: ({ getValue }) => getValue(),
      }
    ],
    []
  )

  return (
    <>
      <Grid container flexDirection='column' spacing={1}>
        <Grid container item alignItems='center' sx={{ pl: 0 }}>
          <Grid item>
            <Button variant='contained' startIcon={<AddIcon />} onClick={handleAdd}>
              Add Drawer Item
            </Button>
          </Grid>
        </Grid>
        <Grid item>
          <ReactTable<DrawerItem>
            columns={columns}
            data={drawerItems}
            useDragAndDrop
            dragHandleHeader=""
            disablePagination
            disableSort
            pageSize={10}
            showActionsColumn
            disabledCopyActionColumn
            onEdit={props => handleEdit(props.row.index)}
            onDelete={props => handleDelete(props.row.index)}
            onDropChange={handleDropChange}
          />
        </Grid>
      </Grid>

      {dialogOpen && editingItem && (
        <DrawerItemDialog
          open={dialogOpen}
          item={editingItem}
          onClose={() => setDialogOpen(false)}
          onSave={handleSave}
          eventFlows={watch('eventFlows') || []}
          drawerItemIndex={editingIndex ?? undefined}
          drawerItems={drawerItems}
          onEventFlowsChange={flows => setValue('eventFlows', flows, { shouldDirty: true })}
        />
      )}
    </>
  )
}
